/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {Observable, of} from 'rxjs';
import {catchError, debounceTime, finalize, map, share, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {LocalizationService} from '../../../shared/localization/localization.service';
import {SortDirection} from '../../../shared/ui/table/table.constants';
import {isSmallDevice} from '../../../shared/utils/browser';
import {Notification, Notifications, SortType} from '../../shared/communications.interface';
import {CommunicationsService} from '../../shared/communications.service';
import {NotificationsService} from '../notifications.service';

interface Sorter {
  title: string;
  direction: SortDirection;
  sortParam: string;
}

@Component({
  selector: 'gp-notifications-list',
  templateUrl: './notifications-list.component.html',
  styleUrls: [
    './notifications-list.component.scss',
    '../notifications-general.styles.scss',
    '../notifications.component.scss']
})
export class NotificationsListComponent extends Destroyable implements OnInit {

  public mainSort: boolean = false;
  public loading: boolean = true;
  public directions: any = SortDirection;

  public total: number = 0;
  public searchInput: UntypedFormControl = new UntypedFormControl();
  private _notifications: Notification[] = [];
  private _sortTypes: SortType[] = [];
  private currentPage: number = 0;
  private orderBy: string = '';
  private search: string = '';

  public selectedNotificationIds: Set<number> = new Set<number>();
  public isSmallDevice = isSmallDevice;
  public trigger$: Observable<number>;

  constructor(private communicationsService: CommunicationsService,
              private notificationsService: NotificationsService,
              private localization: LocalizationService,
              private translate: TranslateService,
              private router: Router) {
    super();
  }

  public ngOnInit() {
    this._sortTypes = [{
      title: 'NOTIFICATIONS.read_first',
      sort: 'read,effectiveDate,id',
      checked: false,
      desc: true
    }, {
      title: 'NOTIFICATIONS.reception_date',
      sort: 'effectiveDate,id',
      checked: true,
      desc: true
    }];
    this.setOrderBy(this.sortTypes.find(type => type.checked));

    this.translate.onLangChange
      .asObservable()
      .pipe(
        startWith(null),
        debounceTime(1000),
        tap(() => {
          this._notifications = [];
          this.currentPage = 0;
        }),
        switchMap(() => this.loadNotifications(false)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.searchInput.valueChanges.pipe(
      debounceTime(500),
      tap(value => this.search = value?.trim()),
      switchMap(() => this.loadNotifications(true)),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
    this.trigger$ = this.localization.changeDateFormatEvent.pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
  }

  public get notifications() {
    return this._notifications;
  }

  public get sortTypes() {
    return this._sortTypes;
  }

  public loadMore() {
    if (this.loading) {
      // Already loading, nothing to do
      return;
    }
    this.currentPage = this.currentPage + 1;
    this.loadNotifications(false).subscribe();
  }

  public selectSortableType(sortType: SortType) {
    if (sortType.checked === true) {
      sortType.desc = !sortType.desc;
    } else {
      this.sortTypes.forEach((type: SortType) => {
        type.checked = false;
        type.desc = true;
      });
      sortType.checked = true;
    }

    this.setOrderBy(sortType);
    this.loadNotifications(true).subscribe();
  }

  public setOrderBy(sortType: SortType) {
    this.orderBy = sortType.desc ? `-${sortType.sort}` : sortType.sort;
  }

  private loadNotifications(isUpdate: boolean = false): Observable<Notifications> {
    if (isUpdate) {
      this._notifications = [];
      this.currentPage = 0;
    }
    this.loading = true;

    return this.notificationsService
      .getNotifications(this.currentPage, this.orderBy, this.search)
      .pipe(
        tap(result => {
          this.total = result.total;
          this._notifications = [].concat(this._notifications).concat(result.notifications);
        }),
        finalize(() => this.loading = false),
        catchError(() => of({total: 0, notifications: []}))
      );
  }

  public toggle(notification: Notification, $event: MouseEvent): void {
    $event.stopPropagation();

    const notificationId = notification.id;
    if (this.isSelected(notification)) {
      this.selectedNotificationIds.delete(notificationId);
    } else {
      this.selectedNotificationIds.add(notificationId);
    }
  }

  public isSelected(notification: Notification): boolean {
    return this.selectedNotificationIds.has(notification.id);
  }

  public hasSelection(): boolean {
    return this.selectionCount() > 0;
  }

  public hasToUnread(): boolean {
    return this.readCount() > 0;
  }

  public hasToRead(): boolean {
    return this.unreadCount() > 0;
  }

  public readCount(): number {
    if (this.search) {
      return this._notifications.filter(notif => notif.read).length;
    } else {
      return Math.max(0, this.total - this.communicationsService.informationUnreadCount);
    }
  }

  public unreadCount(): number {
    if (this.search) {
      return this._notifications.filter(notif => !notif.read).length;
    } else {
      return this.communicationsService.informationUnreadCount;
    }
  }

  public selectionCount(): number {
    return this.selectedNotificationIds.size;
  }

  public clearSelected(): void {
    this.selectedNotificationIds.clear();
  }

  public readAll(): void {
    let idsToUpdate: Set<number>;
    if (this.search) {
      idsToUpdate = new Set(this._notifications.map(notif => notif.id));
    }
    this.updateRead(idsToUpdate, true);
  }

  public unreadAll(): void {
    let idsToUpdate: Set<number>;
    if (this.search) {
      idsToUpdate = new Set(this._notifications.map(notif => notif.id));
    }
    this.updateRead(idsToUpdate, false);
  }

  public readSelected(): void {
    this.updateRead(this.selectedNotificationIds, true);
  }

  public unreadSelected(): void {
    this.updateRead(this.selectedNotificationIds, false);
  }

  private updateRead(notificationIds: Set<number>, read: boolean) {
    this.notificationsService.updateNotificationsStatusByIds(notificationIds ? Array.from(notificationIds) : null, read)
      .pipe(
        tap(() => {
          this.router.navigateByUrl('/inbox', {skipLocationChange: true})
            .then(() => this.router.navigate(['/inbox/notifications']));
        })
      ).subscribe();
  }

  public updateReadSingleNotification(notification: Notification) {
    this._notifications = this._notifications.map(notif => {
      if (notif.id === notification.id) {
        return {...notif, read: true};
      }
      return notif;
    });
  }

  public isRouteActive(notificationId: number): boolean {
    return this.router.isActive(this.router.createUrlTree(['/inbox/notifications', notificationId]), false);
  }

  hasAttachment(notification: Notification): boolean {
    return !!(notification.notificationBodyArgs?.fileName || notification.notificationBodyArgs?.documentName);
  }

  public selectedAttachmentCount(): number {
    return this._notifications
      .filter(notification =>
        this.selectedNotificationIds.has(notification.id) &&
        (notification.notificationBodyArgs?.fileName || notification.notificationBodyArgs?.documentName)
      )
      .length;
  }

  private getFileIdFromNotification(notification: Notification): number | string | null {
    const bodyArgs = notification.notificationBodyArgs;
    const tags = bodyArgs?.['tags'];
    if (tags && typeof tags === 'object' && !Array.isArray(tags)) {
      const fileDescriptionId = (tags as { fileDescriptionId?: number | string }).fileDescriptionId;
      if (fileDescriptionId !== undefined) {
        return fileDescriptionId;
      }
    }

    const documentId = bodyArgs?.['documentId'];
    if (documentId !== undefined && (typeof documentId === 'string' || typeof documentId === 'number')) {
      return documentId;
    }
    return null;
  }

  public downloadSelectedAttachments(): void {
    const selectedFileIds = this._notifications
      .filter(notification => this.selectedNotificationIds.has(notification.id))
      .map(notification => this.getFileIdFromNotification(notification))
      .filter((id): id is number => id !== null)

    if (selectedFileIds.length > 0) {
      this.notificationsService.downloadZipFile(selectedFileIds.map(id => id.toString())).subscribe();
    }
  }
}
